/**
 * DOCUMENTATION:
 * Component: UiUxColorTheory
 * Purpose: Educational page detailing color psychology, accessibility (contrast), and programmatic color models (HSL).
 * * * Note for Junior Developers:
 * - Color communicates status: Red is danger/error, Green is success, Blue is information/trust. Don't break these conventions.
 * - Accessibility is mandatory: If your grey text on a white background fails the WCAG 4.5:1 contrast ratio, visually impaired users cannot read it.
 * - Start using HSL (Hue, Saturation, Lightness) instead of HEX. It makes calculating hover states and dark modes mathematically trivial.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxColorTheory() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Color Theory Concepts
    const colorConcepts = useMemo(() => [
        { concept: "Color Psychology", application: "Emotional Context", description: "Blue builds trust (banks, tech). Red signifies urgency/error. Green indicates success. Yellow grabs attention (warnings)." },
        { concept: "The 60-30-10 Rule", application: "Palette Balancing", description: "A classic design rule: 60% of your UI should be the dominant color (backgrounds), 30% secondary (cards), and 10% accent (buttons/CTAs)." },
        { concept: "Contrast Ratios (WCAG)", application: "Accessibility (a11y)", description: "Text must have a contrast ratio of at least 4.5:1 against its background. Failing this makes your app unusable for visually impaired users." },
        { concept: "Dark Patterns", application: "Ethical UX", description: "Using colors deceptively (e.g., making a 'Cancel Subscription' button a low-contrast grey so users can't find it)." },
    ], []);

    // Coding examples demonstrating how Color translates to CSS
    const codingModules = useMemo(() => [
        {
            title: "1. The 60-30-10 Rule in CSS Variables",
            code: `:root {\n  /* 60% Dominant (Backgrounds) */\n  --color-bg: #f8f9fa;\n  \n  /* 30% Secondary (Surfaces/Cards) */\n  --color-surface: #ffffff;\n  \n  /* 10% Accent (Call to Action Buttons) */\n  --color-accent: #DAA520;\n  \n  /* Text Colors */\n  --color-text-main: #212529;\n}\n\nbody {\n  background-color: var(--color-bg);\n  color: var(--color-text-main);\n}`,
            desc: "Using native CSS variables (`:root`) allows you to define your color palette in one place. It strictly enforces the 60-30-10 rule across your entire application."
        },
        {
            title: "2. The Superiority of HSL vs HEX",
            code: `/* ❌ HEX: Hard to read, impossible to manipulate mathematically */\n.btn {\n  background-color: #3b82f6;\n}\n.btn:hover {\n  background-color: #2563eb; /* Requires a whole new hex code */\n}\n\n/* ✅ HSL: Hue (0-360), Saturation (0-100%), Lightness (0-100%) */\n.btn {\n  /* A nice blue: 217 degrees, 90% saturated, 60% light */\n  background-color: hsl(217, 90%, 60%);\n}\n.btn:hover {\n  /* Just drop the lightness by 10% for a perfect hover state! */\n  background-color: hsl(217, 90%, 50%);\n}`,
            desc: "HEX codes are arbitrary to the human brain. HSL allows developers to easily create darker hover states, pastel variants, or entirely new themes just by adjusting the Lightness or Hue percentages."
        },
        {
            title: "3. Semantic Color Tokens",
            code: `// SCSS Example\n// 1. Define the raw palette (The "What")\n$blue-500: hsl(217, 90%, 50%);\n$red-500:  hsl(0, 84%, 60%);\n$grey-800: hsl(220, 20%, 20%);\n\n// 2. Map them to Semantic Tokens (The "Why")\n$color-primary: $blue-500;\n$color-error:   $red-500;\n$color-text:    $grey-800;\n\n// 3. Use the Semantic Tokens in your CSS\n.alert-box {\n  // If the error color ever changes from Red to Orange, \n  // you only update the variables above. The code below never changes.\n  border-left: 4px solid $color-error;\n}`,
            desc: "Never use raw color variables (like `$red-500`) directly in your component styles. Always map them to semantic tokens (like `$color-error`). This abstraction is what makes large-scale design systems (like Material UI) so flexible."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    COLOR THEORY & PSYCHOLOGY
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Painting with Purpose
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Color is the fastest way to communicate with a user. Before they read a single word of text, their brain processes the colors on the screen. As a UI engineer, you must ensure that your color choices meet accessibility standards, reflect the brand's identity, and guide the user intuitively through the application without causing visual fatigue.
                        </Typography>

                        {/* ✅ Core Philosophies Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Color Application Rules</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Rule</div>
                                    <div>Application Focus</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {colorConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.application}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
